import { useEffect, useRef } from "react";

import { useCreateView } from "@/hooks/mutations/useCreateView";
import { useAddView } from "@/hooks/sets/useAddView";
import { QueryParamsType, View, ViewTableName } from "@/types";

/**
 * A hook that ensures a user has a view for a given table and sets the default view if no views are found.
 */
export function useDefaultViewGuard({
  views,
  selectedView,
  setSelectedView,
  tableName,
  defaultQueryParams,
  preferredViewId,
}: {
  views?: View[];
  selectedView: View | null;
  setSelectedView: (view: View) => void;
  tableName: ViewTableName;
  defaultQueryParams: QueryParamsType;
  preferredViewId?: number | null;
}) {
  /* React Hooks and Cache Updates */
  // Creates
  const { mutate: createView } = useCreateView();
  const addView = useAddView();

  /* React Hooks */
  // useRefs
  // Ensure only one view is added as the default if views are empty on this page for this user
  const isAdding = useRef(false);

  useEffect(() => {
    if (!views) return;

    if (views.length === 0 && !isAdding.current) {
      isAdding.current = true;
      createView(
        {
          createViewRequest: {
            queryParams: defaultQueryParams,
            tableName,
          },
        },
        {
          onSuccess: (newView) => {
            addView({ viewQueryParams: { tableName }, newView });
            setSelectedView(newView);
          },
        },
      );
    } else if (selectedView === null) {
      const preferred =
        preferredViewId != null
          ? views.find((v) => v.id === preferredViewId)
          : undefined;
      setSelectedView(preferred ?? views[0]);
    }
  }, [
    addView,
    createView,
    defaultQueryParams,
    preferredViewId,
    selectedView,
    setSelectedView,
    tableName,
    views,
  ]);
}
